import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { TrackingService } from './tracking.service';
import { LoadingService } from './loading.service';
import { ConfigService } from './config.service';

import { Assignment } from '../structures/assignment';
import { Filter } from '../structures/filter';
import { log } from './decorators/log.decorator';

@Injectable({
    providedIn: 'root'
})
export class AssignmentService {
    constructor(
        private http: HttpClient,
        private trackingService: TrackingService,
        private loadingService: LoadingService,
        private configService: ConfigService
    ) {}

    openContentDialog: Subject<Assignment> = new Subject();
    openErrorDialog: Subject<string> = new Subject();

    refreshTracking: Subject<void> = new Subject();
    refreshedTracking: Subject<void> = new Subject();
    playArtificeAnimation: Subject<boolean> = new Subject();
    playCheckAnimation: Subject<number> = new Subject();
    resetFilters: Subject<void> = new Subject();

    /**
     * Serializes a {@link Filter} into the query params shared by the learner
     * assignment endpoints (list + counts). Array dimensions are pipe-joined; the
     * VA-09/12 chip dimensions (pinned/standardized/featured/subtab) are only sent
     * when set so the backend treats an absent one as "no restriction".
     */
    /**
     * Builds the query params of the `/users/assignments` endpoints from a
     * learner-view `Filter`. Only the filters the learner view (V2.2) can
     * produce are sent: the legacy panel's `startdate`/`enddate`/`favorite` have
     * no equivalent in the new view and were dropped. `origin` — the assignment
     * origins ("Type d'assignation") — is back as of the client Evol request.
     */
    private buildAssignmentParams(filters: Filter, sortMode?: string): any {
        const params: any = {
            search: filters.search ? filters.search : '',
            level: filters.level ? filters.level.join('|') : '',
            referent: filters.referent ? filters.referent.join('|') : '',
            tracking: filters.tracking ? filters.tracking.join('|') : '',
            availability: filters.availability ? filters.availability.join('|') : '',
            origin: filters.origin ? filters.origin.join('|') : '',
            save_filters: filters.save_filters === false ? 'false' : 'true',
            offset: filters.offset ? filters.offset : 0,
            type: filters.type ? filters.type.join('|') : '',
            category: filters.category ? filters.category.join('|') : ''
        };

        if (filters.limit) {
            params.limit = filters.limit;
        }
        if (filters.pinned !== undefined) {
            params.pinned = filters.pinned;
        }
        if (filters.standardized !== undefined) {
            params.standardized = filters.standardized;
        }
        if (filters.featured !== undefined) {
            params.featured = filters.featured;
        }
        if (filters.subtab) {
            params.subtab = filters.subtab;
        }
        if (sortMode) {
            params.sort = sortMode;
        }

        return params;
    }

    @log() getAssignments(filters: Filter, sortMode?: string): Observable<any> {
        const params = this.buildAssignmentParams(filters, sortMode);

        return this.http.get(`/users/assignments`, { params }).pipe(
            map((data: any) => {
                data.tree = data.tree.map((assignment) => this.addCanLaunchAttribut(assignment));
                return data;
            })
        );
    }

    @log() getFullAssignments(): Observable<any> {
        const params: any = {
            save_filters: 'false'
        };

        return this.http.get(`/users/assignments`, { params }).pipe(
            map((data: any) => {
                // Same normalisation as getAssignments: the recursive canLaunch
                // flags (prerequisite/ordering unlocks) are computed client-side.
                data.tree = data.tree.map((assignment) => this.addCanLaunchAttribut(assignment));
                return data;
            })
        );
    }

    /**
     * Top-level tab counters (`GET /users/assignments/counts`) — `{ featured,
     * standardized_parcours, others }`, each `{ total, new }`. Drives which tabs
     * are shown and the per-tab "nouveauté" badge without loading the tree.
     */
    @log() getAssignmentsCounts(filters: Filter = {}): Observable<any> {
        const params = this.buildAssignmentParams({ ...filters, save_filters: false });
        return this.http.get(`/users/assignments/counts`, { params });
    }

    /**
     * Sub-tab (chip) counters (`GET /users/assignments/subtabs/counts`) —
     * `{ all, started, todo, pinned }`, each `{ total, new }`, over root contents
     * excluding normed parcours. Feeds the chip counters/visibility (VA-09/12).
     */
    @log() getAssignmentsSubtabCounts(filters: Filter = {}): Observable<any> {
        const params = this.buildAssignmentParams({ ...filters, save_filters: false });
        return this.http.get(`/users/assignments/subtabs/counts`, { params });
    }

    @log() getGroupAssignmentsCompletion(assignmentsId: Array<number>): Observable<any> {
        const params = {
            assignments_groups_ids: assignmentsId.join('|')
        };
        return this.http.get(`/groups/assignments/completion`, { params });
    }

    getGroupCompletion(groupId) {
        return this.http.get(`/groups/${groupId}/completion`);
    }

    @log() getLastContent(): Observable<any> {
        return this.http.get(`/users/lastContent`);
    }

    @log() getContent(contentId: number): Observable<any> {
        return this.http.get(`/contents/${contentId}`, { params: { log: 'student' } });
    }

    @log() getFilters(): Observable<any> {
        return this.http.get(`/users/assignments/filters`);
    }

    @log() getMyFilters(): Observable<any> {
        return this.http.get(`/assignments/filters`);
    }

    @log() getMyCalendar(params): Observable<any> {
        return this.http.get(`/calendar`, { params });
    }

    /**
     * Calls of the current learner still to come or currently under way (started, slot not
     * over), each carrying its computed `enddate`. Relayed from EASI Connect by uimmws.
     */
    @log() getUpcomingCalls(): Observable<any> {
        return this.http.get(`/calls/upcoming`);
    }

    /**
     * Resolves the one-shot join URL of an upcoming call (EASI Connect reservation),
     * on demand for the learner view's "Rejoindre" action. The `/calls/upcoming` list
     * carries no URL (it lives on the Call reservation detail); uimmws relays this
     * through EASI Connect and returns `{ url }`.
     *
     * @param callId The Call (bbb_reservation) id, positive (un-negated) form.
     */
    @log() getCallJoinUrl(callId: number): Observable<any> {
        return this.http.get(`/calls/${callId}/join`);
    }

    @log() addAssignmentToFavorites(assignment: Assignment): Observable<any> {
        return this.http.post<boolean>(`/users/favorite`, {
            type: 'assignment_user',
            id: assignment.assignmentId,
            action: 'add'
        });
    }

    @log() removeAssignmentFromFavorites(assignment: Assignment): Observable<any> {
        return this.http.post<boolean>(`/users/favorite`, {
            type: 'assignment_user',
            id: assignment.assignmentId,
            action: 'delete'
        });
    }

    @log() getParentOf(contentId: number): Observable<any> {
        return this.http.get(`/users/assignments/${contentId}`);
    }

    @log() uploadDevoir(file: File, assignment: Assignment): Observable<any> {
        const body = new FormData();
        body.append('devoir', file);

        return this.http.post(`/contents/${assignment.id}/submit_work`, body);
    }

    @log() getCertificate(assignment: Assignment, user: any): any {
        return this.http.get(`/assemblies/${assignment.id}/certificate/user/${user.id}`, {
            params: {},
            observe: 'response',
            responseType: 'blob'
        });
    }

    getIcon(assignment: Assignment): string {
        switch (assignment.category) {
            case 'positionnement':
                return 'icon-positionnement-cercle';
            case 'preparation':
                return 'icon-Preparation-cercle';
            case 'ressource':
                return 'icon-ressource-cercle';
            case 'travail':
                return 'icon-Miseenapplcation-cercle';
            case 'evaluation':
                return 'icon-evaluation-cercle';
            case 'corrige':
                return 'icon-Corrige-cercle';
            case 'tp':
                return 'icon-TP-cercle';
            case 'presentiel':
                return 'icon-activite-presentielle-cercle';
            case 'url':
                return 'icon-lien-cercle';
            case 'devoir':
                return 'icon-remise-devoir';
            default:
                switch (assignment.level) {
                    case 'parcours':
                        return 'icon-parcours-cercle';
                    case 'bloc':
                        return 'icon-bloc-cercle';
                    case 'competence':
                        return 'icon-competence-cercle';
                    case 'module':
                        return 'icon-module-cercle';
                    case 'sequence':
                        return 'icon-sequence-cercle';
                }
        }
    }

    /**
     * range les children d'un assignation dans l'ordre de leur ordernumbers
     * @param children liste des assignation enfant
     * @param parentId id du parent
     */
    getChildrenByOrder(children: Array<Assignment>, parentId: number): Array<Assignment> {
        if (!children) {
            return [];
        }
        const result = children
            .filter((child) => child.ordernumbers)
            .sort((a, b) => a.ordernumbers[parentId] - b.ordernumbers[parentId]);

        return result;
    }

    checkIfContentDone(content: Assignment): boolean {
        if (
            content.tracking.status === 'passed' ||
            content.tracking.status === 'completed' ||
            content.tracking.forcedStatus === 'passed' ||
            content.tracking.forcedStatus === 'completed'
        ) {
            return true;
        }
        return false;
    }

    checkAllPreviousContentsDone(
        child: Assignment,
        children: Array<Assignment>,
        assignment: Assignment
    ): boolean {
        if (child.ordernumbers[assignment.id] === 1) {
            return true;
        } else {
            for (const _child of children) {
                if (_child.ordernumbers[assignment.id] < child.ordernumbers[assignment.id]) {
                    if (!this.checkIfContentDone(_child)) {
                        return false;
                    }
                }
            }
            return true;
        }
    }

    addCanLaunchAttribut(assignment: Assignment) {
        // Normalise le flag "nouveauté" du WS (clé `new`, int 0/1) en booléen `is_new` typé, à
        // l'ingestion de l'arbre : le backend a déjà résolu la règle complète (fenêtre 15 j +
        // statut/forçage sur les activités + rollup parent) par nœud (VA-02). On le fait ici car
        // cette récursion visite déjà chaque nœud de l'arbre (racine + enfants).
        assignment.is_new = Number((assignment as { new?: number }).new) === 1;
        if (assignment.canLaunch === undefined) {
            assignment.canLaunch = true;
        }
        if (assignment.canLaunch === false) {
            for (const i in assignment.children) {
                assignment.children[i].canLaunch = false;
            }
        } else {
            if (!assignment.ordered) {
                for (const i in assignment.children) {
                    assignment.children[i].canLaunch = true;
                }
            } else {
                const orderedChildren = this.getChildrenByOrder(assignment.children, assignment.id);
                for (const child of orderedChildren) {
                    child.canLaunch = this.checkAllPreviousContentsDone(
                        child,
                        orderedChildren,
                        assignment
                    );
                }
            }
        }

        for (const i in assignment.children) {
            this.addCanLaunchAttribut(assignment.children[i]);
        }
        return assignment;
    }

    isAssignmentValid(assignment: Assignment): boolean {
        let end: Date;
        if (assignment.timeend !== null) {
            end = new Date(assignment.timeend);
        }

        if (new Date(assignment.timestart).getTime() > new Date().getTime()) {
            return false;
        } else if (end && end.getTime() < new Date().getTime() && assignment.lock_after_enddate) {
            return false;
        } else {
            return true;
        }
    }

    canLaunchContent(assignment: Assignment) {
        return (
            !assignment.launched &&
            !assignment.level &&
            !assignment.isRestricted &&
            !assignment.updateinprogress &&
            (this.isAssignmentValid(assignment) || !assignment.lock_after_enddate) &&
            new Date(assignment.timestart).getTime() < new Date().getTime() &&
            assignment.canLaunch !== false &&
            assignment.type !== 'presentiel' &&
            !(assignment.type === 'scorm' && assignment.tracking.status === 'passed')
        );
    }

    launchContent(assignment: Assignment): void {
        if (this.canLaunchContent(assignment)) {
            assignment.launched = true;
            this.getContent(assignment.id).subscribe((content: any) => {
                if (
                    assignment.type !== 'quiz' &&
                    assignment.tracking.status === 'not attempted' &&
                    !assignment.mark_as_completed_on_open
                ) {
                    this.trackingService.markContentAsOpened(assignment.id).subscribe(() => {
                        assignment.tracking.status = 'opened';
                    });
                }
                if (navigator.userAgent.includes('IONIC-EASI')) {
                    this.launchActivityInNativeApp(content);
                } else {
                    const data: Window = this.launchActivity(content);
                    if (
                        navigator.userAgent.toLowerCase().match(/(ipad|iphone|safari)/) &&
                        navigator.userAgent.search('Chrome') < 0
                    ) {
                        this.emitRefreshTracking();
                    } else {
                        this.loadingService.startLoading('AssignmentComponent');
                        const pollTimer = window.setInterval(() => {
                            if (data.closed !== false) {
                                assignment.launched = false;
                                window.clearInterval(pollTimer);
                                this.emitRefreshTracking();
                                this.loadingService.stopLoading('AssignmentComponent');
                            }
                        });
                    }
                }
            });
        }
    }

    launchActivity(content: Assignment): Window {
        let popup: Window;
        if (content.path !== '') {
            switch (content.type) {
                case 'scorm':
                    popup = window.open(content.path, content.title);
                    break;
                case 'pdf':
                    popup = window.open(content.path, content.title);
                    break;
                case 'url':
                    popup = window.open(content.url, content.title);
                    break;
                case 'quiz':
                    popup = window.open(
                        `${this.configService.getFrontEndPoint()}#/quizPlayer/${content.id}`,
                        '_blank'
                    );
                    break;
                case 'video':
                    popup = window.open(
                        `${this.configService.getFrontEndPoint()}#/videoPlayer/${content.id}`,
                        '_blank'
                    );

                    break;
                case 'h5pactivity':
                case 'testcognitifn4s':
                    popup = window.open(
                        `${this.configService.getFrontEndPoint()}#/h5pPlayer/${content.id}`,
                        '_blank'
                    );

                    break;
                case 'presentiel':
                    this.openContentDialog.next(content);
                    break;
                default:
                    popup = window.open(content.path, '_blank');
            }
        } else {
            this.openErrorDialog.next('Cette activité est nationale, vous ne pouvez pas la lancer');
        }

        if (!popup) {
            this.openErrorDialog.next(
                "Vous utilisez un bloqueur de pop-up qui vous empêche d'ouvrir un nouvel onglet. <br >Veuillez modifier les paramètres de votre navigateur pour autoriser les pop-up pour le site : <br><strong>easi-training.fr</strong>"
            );
        }
        return popup;
    }

    launchActivityInNativeApp(content: Assignment) {
        if (content.path !== '') {
            switch (content.type) {
                case 'scorm':
                    window.top.postMessage({ nativeWrapperRedirect: content.path }, '*');
                    break;
                case 'pdf':
                    window.top.postMessage({ nativeWrapperRedirect: content.path }, '*');
                    break;
                case 'url':
                    window.top.postMessage({ nativeWrapperRedirect: content.url }, '*');
                    break;
                case 'quiz':
                    window.top.postMessage(
                        {
                            nativeWrapperRedirect: `${this.configService.getFrontEndPoint()}#/quizPlayer/${
                                content.id
                            }`
                        },
                        '*'
                    );
                    break;
                case 'video':
                    window.top.postMessage(
                        {
                            nativeWrapperRedirect: `${this.configService.getFrontEndPoint()}#/videoPlayer/${
                                content.id
                            }`
                        },
                        '*'
                    );

                    break;
                case 'h5pactivity':
                case 'testcognitifn4s':
                    window.top.postMessage(
                        {
                            nativeWrapperRedirect: `${this.configService.getFrontEndPoint()}#/h5pPlayer/${
                                content.id
                            }`
                        },
                        '*'
                    );

                    break;
                case 'presentiel':
                    this.openContentDialog.next(content);
                    break;
                default:
                    window.top.postMessage({ nativeWrapperRedirect: content.path }, '*');
            }
        } else {
            this.openErrorDialog.next('Cette activité est nationale, vous ne pouvez pas la lancer');
        }
    }

    emitResetFilters(): void {
        this.resetFilters.next();
    }

    emitRefreshTracking(): void {
        this.refreshTracking.next();
    }

    emitRefreshedTracking(): void {
        this.refreshedTracking.next();
    }

    emitPlayArtificeAnimation($event) {
        this.playArtificeAnimation.next($event);
    }

    emitPlayCheckAnimation($event) {
        this.playCheckAnimation.next($event);
    }
}
